import { useUpdateEffect } from 'ahooks';
import { FC, ReactElement, useCallback, useState } from 'react';
import { useIsFetching, useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { DomainRegluingsQueryKeys, SiteDomainsQueryKeys, useDeleteDomainRegluingsMutation, useGetAllDomainRegluingsQuery } from '@/modules/Domains';

import { useAppPagination } from '@/common/hooks';
import { DomainRegluingsView } from './DomainRegluingsOverviewView';

interface Props {
  siteId: string;
  domainId: string;
}

export const DomainRegluingsOverviewContainer: FC<Props> = ({ siteId, domainId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [isRegluingFormVisible, setIsRegluingFormVisible] = useState(false);
  const { pageParam: currentPage } = useAppPagination();

  const {
    data: regluingsData,
    error: regluingsError,
    isError: isRegluingsError,
    isLoading: isRegluingsLoading
  } = useGetAllDomainRegluingsQuery(siteId, domainId)();

  const { mutate: deleteDomainRegluingById, isLoading: isDeleteRegluingLoading } = useDeleteDomainRegluingsMutation(
    siteId,
    domainId
  )({
    onSuccess: async () => {
      await queryClient.refetchQueries([DomainRegluingsQueryKeys.GetAllRegluings, siteId, domainId]);

      await queryClient.refetchQueries([SiteDomainsQueryKeys.GetAllDomains, siteId, { page: currentPage }]);

      appMessageApi.success('Переклейку успішно видалено');
    },
    onError: (err) => {
      messageOnError(err, appMessageApi);
    }
  });

  useUpdateEffect(() => {
    setIsRegluingFormVisible(false);
  }, [siteId, domainId]);

  const handleToggleRegluingFormVisibility = useCallback((): void => {
    setIsRegluingFormVisible((prev) => !prev);
  }, []);

  const handleDeleteRegluing = useCallback(
    (regluingId: string) => () => {
      deleteDomainRegluingById(regluingId);
    }, []
  );

  const isSiteDomainLoading = !!useIsFetching([SiteDomainsQueryKeys.GetByIdDomains, siteId, domainId], { exact: true });

  return (
    <FallbackedLayout
      data={ regluingsData }
      error={ regluingsError }
      isError={ isRegluingsError }
      isLoading={ isRegluingsLoading }
    >
      { (regluings): ReactElement => (
        <>
          <DomainRegluingsView
            domainId={ domainId }
            siteId={ siteId }
            regluings={ regluings }
            isRegluingsLoading={ isRegluingsLoading }
            isRegluingFormVisible={ isRegluingFormVisible }
            isButtonsDisabled={ isSiteDomainLoading || isDeleteRegluingLoading }
            handleToggleRegluingFormVisibility={ handleToggleRegluingFormVisibility }
            handleDeleteRegluing={ handleDeleteRegluing }
          />
        </>
      ) }
    </FallbackedLayout>
  );
};
